import React, { ContextType, useContext, useEffect, useMemo } from "react";

import { CssBaseline, ThemeProvider } from "@material-ui/core";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { Route, Routes, useNavigate, useParams } from "react-router";

import BusinessQuery from "api/queries/BusinessQuery";
import Favicon from "components/Favicon";
import Loading from "components/Loading";
import config from "config";
import MenuScreen from "containers/MenuScreen";
import { AppContext } from "contexts/AppContext";
import BusinessContext from "contexts/BusinessContext";
import useQuery from "hooks/useQuery";
import useTracking from "hooks/useTracking";
import theme from "styles/theme";

const hostParts = window.location.host.split(".");
const businessSlug = hostParts.length > 2 ? hostParts[0] : undefined;

if (!businessSlug) {
	window.location.href = "https://www.hyperflyer.com";
}

const BusinessApp = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const params = useParams();
	const { locale, setLocale } = useContext(AppContext);
	const { trackPageEvent } = useTracking();

	const { loading, data } = useQuery(BusinessQuery, {
		fetchPolicy: "cache-and-network",
		variables: {
			businessInput: {
				businessSlug
			}
		}
	});

	useEffect(() => {
		if (locale !== params.locale) {
			setLocale(params.locale);
			navigate(["", locale, params["*"]].filter(Boolean).join("/"));
		}
	}, [params, locale, setLocale, navigate]);

	const stripePromise = useMemo(() => {
		return loadStripe(config!.STRIPE_ID!, { stripeAccount: data?.business?.stripeAccountId || undefined });
	}, [data?.business?.stripeAccountId || undefined]);

	useMemo(() => {
		if (data && data.business) {
			if (data.business.locales.includes(params.locale) && locale !== params.locale) {
				setLocale(params.locale);
			}

			if (data.business.locales.length > 0) {
				setLocale(params.locale);
				return { locale: data.business.locales[0] };
			}
		}
		return { locale: "en" };
	}, [locale, setLocale, data, params.locale]);

	const businessContext = useMemo<ContextType<typeof BusinessContext>>(
		() => ({ business: data ? data.business : (null as any) }),
		[data]
	);

	if (!data && loading) {
		return <Loading />;
	}

	if (!data) {
		return <h3>{t("businessNotFound", { businessSlug })}</h3>;
	}

	const { business } = data;

	const onChangeClientState = ({ title }) => {
		trackPageEvent(title);
	};

	return (
		<ThemeProvider theme={theme("light", business?.theme)}>
			<Helmet onChangeClientState={onChangeClientState} />

			<CssBaseline />
			<Favicon faviconUrl={business?.faviconUrl} />
			<BusinessContext.Provider value={businessContext}>
				<Elements stripe={stripePromise}>
					<Routes>
						<Route path="//*" element={<MenuScreen />} />
					</Routes>
				</Elements>
			</BusinessContext.Provider>
		</ThemeProvider>
	);
};

export default BusinessApp;
